'use client';

import { trackEvent } from '@tuic/tracking';
import { FC } from 'react';
import { getAvailableLocales, Locale, swapLocaleInPath } from '@tuic/i18n';
import { usePathname, useRouter } from 'next/navigation';
import { LegacyIcon, type LegacyIconName } from '../LegacyIcon';
import { RadixSelect, RadixSelectItem } from '../RadixSelect';
import { getCookie, setCookie } from '../../../utils/cookies';
import { I18NInterface } from '../../../utils/i18n';
type ProfileSaveLocaleAction = any; // Bitte keine abhängigkeit zu api-kit

type Props = {
  locale: Locale;
  i18n?: I18NInterface<'changeLanguage'>;
  action?: ProfileSaveLocaleAction;
  icon?: LegacyIconName;
};
const DEFAULT_LOCALE_COOKIE_NAME = 'user_locale';
export const LocaleSwitch: FC<Props> = ({
  locale: currentLocale,
  icon,
  i18n
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const changeHandler = async (value: Locale) => {
    const localeCookie = getCookie(DEFAULT_LOCALE_COOKIE_NAME);
    if (localeCookie && localeCookie !== value) {
      setCookie(DEFAULT_LOCALE_COOKIE_NAME, value, 365);
    }
    trackEvent({
      event_name: 'click',
      click_label: `navi.button.sprachauswahl-${value}`
    });

    //necessary for a11y
    const html = document.querySelector('html');
    if (html) {
      html.lang = value;
    }
    let url = getUrlForLocale(value, pathname);
    router.push(url);
  };
  return <div className="flex cursor-pointer items-center gap-0 text-secondary-marine-100 hover:text-[#7390C5] min-[520px]:gap-2 lg:mr-8" data-sentry-component="LocaleSwitch" data-sentry-source-file="LocaleSwitch.tsx">
      {icon && <div aria-hidden={true}>
          <LegacyIcon className="size-5" name={icon} size={20} />
        </div>}
      <RadixSelect triggerClassName="font-normal max-[519px]:gap-0 text-[18px] leading-[1.4em] min-[520px]:gap-2" freeFloating variant="inline" placeholder={currentLocale.toUpperCase()} displayValue={currentLocale.toUpperCase()} align="end" withFlexibleWidth defaultValue={currentLocale} onValueChange={changeHandler} ariaLabel={i18n?.changeLanguage ?? 'Change the language'} ariaLabelValue={getLocaleLabel(currentLocale, currentLocale)} data-sentry-element="RadixSelect" data-sentry-source-file="LocaleSwitch.tsx">
        {getAvailableLocales().map(locale => <RadixSelectItem className="no-underline min-w-[130px] hover:text-[#7390C5]" key={locale} value={locale} textValue={locale.toUpperCase()}>
            <span aria-label={getLocaleLabel(currentLocale, locale)}>
              {locale.toUpperCase()}
            </span>
          </RadixSelectItem>)}
      </RadixSelect>
    </div>;
};
const getLocaleLabel = (currentLocale: Locale, localeToLocalize: Locale): string => {
  const localeMap = {
    de: {
      de: 'Deutsch',
      en: 'Englisch'
    },
    en: {
      de: 'German',
      en: 'English'
    }
  };
  return localeMap?.[currentLocale]?.[localeToLocalize] ?? localeToLocalize;
};
const getUrlForLocale = (locale: Locale, pathname: string | null): string => {
  pathname = typeof pathname !== 'string' ? '/' : pathname;
  if (typeof window === 'undefined') {
    return swapLocaleInPath(pathname, locale);
  }
  let alternate = window.document.querySelector(`link[rel="alternate"][hrefLang="${locale}"]`);
  if (!alternate) {
    return swapLocaleInPath(pathname, locale);
  }
  return alternate.getAttribute('href') ?? swapLocaleInPath(pathname, locale);
};